import React from 'react';
import { styled } from '@mui/material/styles';
import ResourceTable, { IColumn } from '../../components/ResourceTable';
import AddIcon from '@mui/icons-material/Add';
import { CompanyScoringState, TransactionDto } from 'models/dtoTypes';
import {
  DeleteTransaction,
  GetAllTransactionScores,
  getTransactionDetail
} from 'services/transaction';
import Notice from '../../components/Notice';
import Button from '@mui/material/Button';
import { useHistory } from 'react-router';
import CurrentUser from 'auth/currentUser';
import GetTransactionScoreReport from '../../../utils/getTransactionReport';
import { datetime2str } from 'utils/date';
import Link from 'components/Link';
import { getKosmosReportLink } from 'utils/report';
import generateNonce from 'utils/generateNonce';
import { pollAsync } from 'utils/polling';

const PREFIX = 'TransactionListPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    }
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export default function TransactionListPage({ isAdmin }: { isAdmin?: boolean }): JSX.Element {

  const history = useHistory();
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const [nonce, setNonce] = React.useState('');
  const organisationId = CurrentUser.getInstance().getOrganisationId();

  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  const handleDeleteTransaction = async (model: TransactionDto) => {
    try {
      await DeleteTransaction(model.id || '');
      setNonce(generateNonce());
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: 'Cannot delete organisation wome application',
        severity: 'error',
      });
    }
  };

  const handlePollingTransaction = async(model: TransactionDto, shouldCancel?: () => boolean) => {
    const transactionId = model.id || '';
    try {
      const pollFn = async () => {
        return (await getTransactionDetail(transactionId));
      };
      const isFinished = (company: TransactionDto) => {
        return (shouldCancel && shouldCancel()) || company.scoringState === CompanyScoringState.Completed;
      };
      // start polling every 10 seconds
      return await pollAsync<TransactionDto>(
        pollFn,
        isFinished,
        10
      );
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: 'Cannot rescore the company - ',
        severity: 'error',
      });
    }
    return model;
  };

  const handleDownloadReport = async (transaction: TransactionDto) => {
    try {
      const transactionId = transaction.id || '';
      const reportDate = transaction.lastScoredAt || '';
      const filename = `${transactionId} - Transaction Report -${reportDate}.pdf`;
      await GetTransactionScoreReport(transactionId, filename);
      setNoticeData({
        message: 'A report should be downloaded for this transaction.',
        severity: 'success',
      });
    } catch (e) {
      console.error(e);
      const errorMsg = e instanceof Error ? e.message : '';
      setNoticeData({
        message: 'Cannot download the report for this transaction, error: ' + errorMsg,
        severity: 'error',
      });
    }
  };


  const generateColumns = (): IColumn<TransactionDto>[] => [
    {
      label: 'Financial Product Reference',
      dataIndex: 'financialProductReference',
      sortIndex: 'financialProductReference',
    },
    {
      label: 'Financial Product',
      dataIndex: 'financialProduct',
      sortIndex: 'financialProduct',
    },
    {
      label: 'Buyer',
      dataIndex: 'buyer',
      sortIndex: 'buyerName',
      render: (report) => {
        const companyId = report.buyerId || '';
        const companyName = report?.buyer?.name || report?.buyerId || '';
        const orgId = report.organisationId || '';
        if (report?.scoringState === 'Completed') {
          return <Link to={getKosmosReportLink(companyId, orgId)} text={companyName}/>;
        } else {
          return companyName || 'Unknown';
        }
      },
    },
    {
      label: 'Seller',
      dataIndex: 'seller',
      sortIndex: 'sellerName',
      render: (report) => {
        const companyId = report.sellerId || '';
        const companyName = report?.seller?.name || report?.sellerId || '';
        const orgId = report.organisationId || '';
        if (report?.scoringState === 'Completed') {
          return <Link to={getKosmosReportLink(companyId, orgId)} text={companyName}/>;
        } else {
          return companyName || 'Unknown';
        }
      },
    },
    {
      label: 'Transaction Score',
      dataIndex: 'transactionScore',
      render: (model: TransactionDto) => {
        switch (model.scoringState) {
          case CompanyScoringState.Completed:
            return (
              <Link text={`${model.transactionScore || ''}`} onClick={() => handleDownloadReport(model)} />
            );
          case CompanyScoringState.Error:
            return model.scoringState || '';
          default:
            return 'In Progress';
        }
      }
    },
    {
      label: 'Last Scored At',
      sortIndex: 'lastScoredAt',
      render: (transaction) => {
        const lastScoredAt: string = transaction.lastScoredAt || '';
        return datetime2str(lastScoredAt);
      },
    },
    {
      label: 'Action',
      actions: [
        {
          label: 'Polling',
          hide: (model: TransactionDto) => model.scoringState === CompanyScoringState.Completed || model.scoringState === CompanyScoringState.Error,
          onClick: handlePollingTransaction,
          startOnDefault: true,
        },
        {
          label: 'Download',
          onClick: handleDownloadReport
        },
        {
          label: 'Remove',
          onClick: handleDeleteTransaction
        },
      ],
    }
  ];


  return (
    <Root>
      <div className={classes.pageHeader}>
        <h2>Transactions</h2>
        <Button onClick={() => history.push('/transactions/new')} variant="contained" startIcon={<AddIcon />}>
          Create
        </Button>
      </div>
      <ResourceTable
        key={nonce}
        columns={generateColumns()}
        onFetch={GetAllTransactionScores}
        defaultQuery={!isAdmin
          ? [{
            column: 'organisationId',
            value: organisationId,
          }]
          : undefined}
      />
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}
